import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useImperativeHandle,
} from "react";
import HelpText from "../helpText/HelpText";
import Icon from "../../icons/Icon";
import { ButtonBox } from "../../button/Button";
import styles from "./inputBox.module.scss";
import { Search } from "../../icons/40";
import { XcircleG700, Eye, EyeOff } from "../../icons/18";

// 인풋 타입 종류 정의
type TInputType =
  | "text"
  | "search"
  | "password"
  | "fakeText"
  | "fakeSelect"
  | "email"
  | "tel"
  | "price"
  | "number"
  | "time";

// 전달된 타입에 따라 실제 input에 match 시킬 항목들 지정
const inputWithType: {
  type: TInputType;
  actualType: "text" | "password" | "search" | "email" | "time";
  attributes?: { [key: string]: any };
}[] = [
  { type: "text", actualType: "text" },
  { type: "email", actualType: "email", attributes: { inputMode: "email" } },
  { type: "password", actualType: "password" },
  { type: "search", actualType: "search", attributes: { inputMode: "search" } },
  {
    type: "fakeText",
    actualType: "text",
    attributes: { readOnly: true, inputMode: "none" },
  },
  {
    type: "fakeSelect",
    actualType: "text",
    attributes: { readOnly: true, inputMode: "none" },
  },
  {
    type: "tel",
    actualType: "text",
    attributes: { inputMode: "numeric", pattern: "[0-9]*" },
  },
  {
    type: "price",
    actualType: "text",
    attributes: { inputMode: "numeric", pattern: "[0-9]*" },
  },
  {
    type: "number",
    actualType: "text",
    attributes: { inputMode: "numeric", pattern: "[0-9]*" },
  },
  {
    type: "time",
    actualType: "time",
    attributes: { inputMode: "time" },
  },
];

export type TResultObj = {
  result: "success" | "error" | "";
  type: "dataValid" | "formatValid" | "all" | "";
  label?: string;
  message?: string;
  item?: any;
};

export interface IInputBoxProps {
  type?: TInputType;
  buttonComponent?: React.ReactNode;
  preComponent?: React.ReactNode;
  postComponent?: React.ReactNode;
  helpTextComponent?: React.ReactNode;
  showHelpTextError?: boolean;
  showHelpTextSuccess?: boolean;
  showErrorBorder?: boolean;
  showSuccessBorder?: boolean;
  resultObj?: TResultObj;
  [key: string]: any;
}

export const makeValidationObj = ({
  result,
  type,
  label,
  message,
  extra,
}: {
  result?: boolean;
  type?: "format" | "data" | "all";
  label?: string;
  message?: string;
  extra?: any;
}): TResultObj => {
  const resultObj: TResultObj = {
    result: result === undefined ? "" : result ? "success" : "error",
    type: !type ? "" : type === "format" ? "formatValid" : type === "data" ? "dataValid" : "all",
    label: label ?? "",
    message: message ?? "",
  };
  if (extra) {
    resultObj.item = extra;
  }
  return resultObj;
};

/**
 * Input Box
 *
 * @param ref input ref
 * @param [key:string] any input 요소에 추가할 속성
 * @param type input 타입
 * @param buttonComponent 버튼 컴포넌트
 * @param preComponent 인풋 앞에 붙는 컴포넌트
 * @param postComponent 인풋 뒤에 붙는 컴포넌트
 * @param helpTextComponent 헬프 텍스트 컴포넌트
 * @param showHelpTextError 에러 메시지 표시 여부
 * @param showHelpTextSuccess 성공 메시지 표시 여부
 * @param showErrorBorder 에러 결과 테두리 표시 여부
 * @param showSuccessBorder 성공 결과 테두리 표시 여부
 * @param resultObj 결과값 객체
 */

const InputBox = React.memo(
  React.forwardRef<HTMLInputElement, IInputBoxProps>(
    (
      {
        type = "text",
        buttonComponent,
        preComponent,
        postComponent,
        helpTextComponent,
        resultObj = { result: "", type: "", message: "" },
        showErrorBorder = false,
        showSuccessBorder = false,
        showHelpTextError = false,
        showHelpTextSuccess = false,
        ...otherProps
      },
      ref,
    ) => {
      // input ref
      const inputRef = useRef<HTMLInputElement | null>(null);
      // 전달된 ref가 있을 경우 inputRef와 연결
      useImperativeHandle(ref, () => {
        if (inputRef.current === null) {
          throw new Error("inputRef is not assigned yet");
        }
        return inputRef.current;
      });

      // 현재 input 타입 (비밀번호 <-> 텍스트 같이 변하는 케이스가 있어서 state로 관리)
      const [currentType, setCurrentType] = useState(
        inputWithType.find((item) => item.type === type)?.actualType || "text",
      );

      const [currentAttributes, setCurrentAttributes] = useState(
        inputWithType.find((item) => item.type === type)?.attributes || {},
      );

      // input 앞/뒤 요소 참조 및 너비(해당 값만큼 padding 설정)
      const inputPreRef = useRef<HTMLDivElement | null>(null);
      const inputPostRef = useRef<HTMLDivElement | null>(null);
      const [inputPreWidth, setInputPreWidth] = useState(0);
      const [inputPostWidth, setInputPostWidth] = useState(0);

      useEffect(() => {
        if (inputPreRef?.current) {
          setInputPreWidth(
            inputPreRef?.current?.offsetWidth ? inputPreRef.current.offsetWidth + 6 : 0,
          );
        }
        if (inputPostRef?.current) {
          setInputPostWidth(
            inputPostRef?.current?.offsetWidth ? inputPostRef.current.offsetWidth + 10 : 0,
          );
        }
      }, [inputPostRef, inputPreRef]);

      // input 초기화 (x 버튼 클릭)
      const handleClearInput = useCallback(
        (e: React.MouseEvent<Element>) => {
          let input = e?.currentTarget?.parentElement?.parentElement?.querySelector("input");
          if (!input) return;
          // onChange 이벤트 / onBlur 이벤트가 있을 시 함께 실행
          otherProps?.onChange &&
            otherProps.onChange({
              target: { value: "" },
            } as React.ChangeEvent<HTMLInputElement>);
          otherProps?.onBlur &&
            otherProps.onBlur({
              target: input,
            } as React.FocusEvent<HTMLInputElement>);
        },
        [otherProps?.onChange, otherProps?.onBlur],
      );

      // input 타입 변경 (비밀번호 <-> 텍스트)
      const handleToggleInputType = useCallback(() => {
        setCurrentType(currentType === "password" ? "text" : "password");
      }, [currentType]);

      // input focus 시 (padding 조절 및 안드로이드에서 위치 이동)
      const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
          let input = e.currentTarget;
          if (inputRef?.current && inputPreRef?.current && !inputRef?.current?.readOnly) {
            if (inputPreWidth) {
              inputRef.current.style.paddingLeft = inputPreWidth - 1 + "px";
            }
            if (inputPostWidth) {
              inputRef.current.style.paddingRight = inputPostWidth - 1 + "px";
            }
          }

          if (!/iPhone/.test(navigator.userAgent)) {
            setTimeout(() => {
              if (!input?.readOnly) {
                // input.scrollIntoView({ behavior: "auto", block: "end", inline: "nearest" });
              }
            }, 300);
          }
          otherProps?.onFocus && otherProps.onFocus(e);
        },
        [inputPreWidth, inputPostWidth, otherProps?.onFocus],
      );

      // input onChange
      const handleChnage = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          otherProps?.onChange && otherProps.onChange(e);
        },
        [otherProps?.onChange],
      );

      // input onBlur
      const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
          if (inputPreRef?.current && inputRef?.current) {
            if (inputPreWidth) {
              inputRef.current.style.paddingLeft = inputPreWidth + "px";
            }
            if (inputPostWidth) {
              inputRef.current.style.paddingRight = inputPostWidth + "px";
            }
          }
          otherProps?.onBlur && otherProps.onBlur(e);
        },
        [otherProps?.onBlur, inputPreWidth, inputPostWidth],
      );

      // 결과 객체 변경 시 상태 변경
      const [currentResultObj, setCurrentResultObj] = useState(resultObj);
      useEffect(() => {
        if (resultObj && currentResultObj) {
          const check = Object.keys(resultObj).some((key) => {
            if (resultObj[key] !== currentResultObj[key]) {
              return true;
            }
          });
          if (check) {
            setCurrentResultObj(resultObj);
          }
        }
      }, [resultObj]);

      /**
       * 하위 컴포넌트들 (PreComponent, PostComponent, InputBox, HelpText, ButtonComponent) useMemo로 선언
       */
      // Pre Component
      const PreComponent = useMemo(() => {
        return preComponent ? <>{preComponent}</> : null;
      }, [preComponent]);

      // Post Component
      const PostComponent = useMemo(() => {
        return postComponent ? <>{postComponent}</> : null;
      }, [postComponent]);

      // Input Box
      const InputBox = useMemo(() => {
        return (
          <div
            className={`${styles["input-box"]} ${otherProps?.disabled ? styles["input-box-disabled"] : ""}`}
          >
            <span className={styles["input-pre"]} ref={inputPreRef}>
              {type === "search" && <Icon IconComponent={<Search />} size={40} />}
              {PreComponent}
            </span>
            <input
              {...otherProps}
              {...currentAttributes}
              type={currentType}
              ref={inputRef}
              value={otherProps?.value || ""}
              className={`${type === "fakeSelect" ? `${styles["select"]} ${styles["select-full"]}` : `${styles["input-full"]} ${styles["input-text"]}`} 
              ${otherProps?.readOnly && type.indexOf("fake") === -1 ? styles["input-readonly"] : ""} 
              ${otherProps?.readOnly && type.indexOf("fakeSelect") > -1 ? styles["select-readonly"] : ""} 
              ${showErrorBorder && currentResultObj?.message && currentResultObj?.result === "error" ? styles["error"] : ""} 
              ${showSuccessBorder && currentResultObj?.message && currentResultObj?.result === "success" ? styles["success"] : ""}
              ${otherProps?.className ? otherProps.className : ""}`}
              style={{
                ...otherProps.style,
                textAlign: type === "price" ? "right" : "",
                paddingLeft: inputPreWidth ? inputPreWidth : "",
                paddingRight: inputPostWidth ? inputPostWidth : "",
              }}
              onChange={handleChnage}
              onBlur={handleBlur}
              onFocus={handleFocus}
            />
            <span className={styles["input-post"]} ref={inputPostRef}>
              {/* input type에 따라 버튼 추가 */}
              {(type === "password" || type === "search") && (
                <ButtonBox
                  type="icon"
                  onClick={handleClearInput}
                  style={{
                    opacity: otherProps?.value ? 1 : 0,
                  }}
                >
                  <Icon IconComponent={<XcircleG700 />} size={18} />
                </ButtonBox>
              )}
              {type === "password" && (
                <ButtonBox type="icon" onClick={handleToggleInputType}>
                  {currentType === "password" ? (
                    <Icon IconComponent={<Eye />} size={18} />
                  ) : (
                    <Icon IconComponent={<EyeOff />} size={18} />
                  )}
                </ButtonBox>
              )}
              {PostComponent}
            </span>
          </div>
        );
      }, [
        currentType,
        inputRef,
        showErrorBorder,
        currentResultObj,
        showSuccessBorder,
        handleChnage,
        handleBlur,
        handleFocus,
        handleClearInput,
        handleToggleInputType,
        type,
        otherProps,
        PreComponent,
        PostComponent,
        currentAttributes,
        inputPreWidth,
        inputPostWidth,
      ]);

      // Button Component
      const ButtonComponent = useMemo(() => {
        return <div className={styles["input-button"]}>{buttonComponent}</div>;
      }, [buttonComponent]);

      return (
        <div className={styles["input-box-wrap"]}>
          {/* 인풋 필드 */}
          <div className={styles["input-box-field"]}>
            {/* 인풋 박스 */}
            {InputBox}
            {/* 버튼 컴포넌트 */}
            {buttonComponent && <>{ButtonComponent}</>}
          </div>
          {/* 헬프텍스트 */}
          <HelpText
            resultObj={resultObj}
            showHelpTextError={showHelpTextError}
            showHelpTextSuccess={showHelpTextSuccess}
            helpTextComponent={helpTextComponent}
          />
        </div>
      );
    },
  ),
);

export default InputBox;
